import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueries } from 'react-query';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { Main as Container } from 'Components/Main';
import { PageHelper } from 'Components/PageHelper';
import { NoData } from 'Components/NoData';
import { getGapAnalysis } from 'services/gap-analysis';
import { useAuthTenant } from 'store/auth';
import { PageHeader } from 'Components/ui';

const INK = 'var(--vo-ink)';
const MUTED = 'var(--vo-slate)';
const BLUE = 'var(--vo-celeste)';
const BLUE_DEEP = 'var(--vo-cobalto)';
const BG = 'var(--vo-mist)';
const TEXT = 'var(--vo-ink-2)';

/* ── Page header ── */
const AddBtn = styled.button`
  height: 40px;
  background: ${BLUE};
  color: white;
  border: none;
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  flex-shrink: 0;
  &:hover { background: ${BLUE_DEEP}; }
`;

/* ── Info banner ── */
const InfoBanner = styled.div`
  background: white;
  border-radius: 14px;
  padding: 16px 20px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  border-left: 4px solid ${BLUE};
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 20px;
`;
const InfoTitle = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: ${INK};
  margin: 0 0 2px;
`;
const InfoDesc = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: ${MUTED};
  margin: 0;
  line-height: 1.55;
`;

/* ── Table ── */
const TableCard = styled.div`
  background: white;
  border-radius: 14px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  overflow: hidden;
`;
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;
const THead = styled.thead`
  background: ${BG};
`;
const Th = styled.th`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: ${MUTED};
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 12px 16px;
  text-align: ${({ $center }) => $center ? 'center' : 'left'};
  border-bottom: 1px solid var(--vo-cloud);
  white-space: nowrap;
`;
const Tr = styled.tr`
  border-bottom: 1px solid ${BG};
  cursor: pointer;
  &:hover { background: var(--vo-capri-25); }
  &:last-child { border-bottom: none; }
`;
const Td = styled.td`
  padding: 13px 16px;
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: ${MUTED};
`;
const TdName = styled.td`
  padding: 13px 16px;
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: ${INK};
`;
const TdStatus = styled.td`
  padding: 13px 16px;
`;
const StatusWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;
const StatusDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`;
const StatusLabel = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  color: ${TEXT};
`;
const TdDesc = styled(Td)`
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const ActionCell = styled.td`
  padding: 13px 16px;
  text-align: center;
`;
const ActionsWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
`;
const IconBtn = styled.button`
  width: 30px;
  height: 30px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${MUTED};
  &:hover {
    background: var(--vo-info-10);
    color: ${BLUE};
  }
`;

const STATUS_COLORS = {
  pending:    'var(--vo-warning)',
  inProgress: 'var(--vo-capri)',
  completed:  'var(--vo-success)',
  cancelled:  'var(--vo-danger)',
  paused:     'var(--vo-steel)',
};

const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

export const GapAnalysis = () => {
  const { t } = useTranslation(['gap-analysis', 'common']);
  const navigate = useNavigate();
  const selectedTenant = useSelector(useAuthTenant);

  const tourSteps = useMemo(() => [
    { target: 'body', content: t('gap-analysis:tour.list.step0'), disableBeacon: true, disableScrolling: true, placement: 'center' },
    { target: '[data-tour="page-title"]', content: t('gap-analysis:tour.list.step1'), disableScrolling: true },
    { target: '[data-tour="add-button"]', content: t('gap-analysis:tour.list.step2'), disableScrolling: true },
    { target: '[data-tour="info-card"]', content: t('gap-analysis:tour.list.step3'), disableScrolling: true },
    { target: '[data-tour="table"]', content: t('gap-analysis:tour.list.step4'), disableScrolling: false },
    { target: 'body', content: t('gap-analysis:tour.list.step5'), disableScrolling: true, placement: 'center' },
  ], [t]);

  const results = useQueries([
    {
      queryKey: ['companyKey', selectedTenant.slug],
      queryFn: () => getGapAnalysis(selectedTenant.slug),
      enabled: !!selectedTenant.slug,
    },
  ]);

  const rows = results[0].data?.data || [];
  const isLoading = results[0].isLoading;

  return (
    <Container>
      <PageHelper steps={tourSteps} tourKey="gap-analysis-list" />

      <PageHeader
        data-tour="page-title"
        title={t('gap-analysis:page.title')}
        segments={[
          { label: t('common:breadcrumbs.analysis') },
          { label: t('gap-analysis:page.title') },
        ]}
        actions={
          <AddBtn data-tour="add-button" onClick={() => navigate('/gap-analysis/add')}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            {t('gap-analysis:page.addButton')}
          </AddBtn>
        }
      />

      <InfoBanner data-tour="info-card">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={BLUE} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
          <line x1="18" y1="20" x2="18" y2="10"/>
          <line x1="12" y1="20" x2="12" y2="4"/>
          <line x1="6" y1="20" x2="6" y2="14"/>
        </svg>
        <div>
          <InfoTitle>{t('gap-analysis:page.info.title')}</InfoTitle>
          <InfoDesc>{t('gap-analysis:page.info.description')}</InfoDesc>
        </div>
      </InfoBanner>

      {!isLoading && rows.length === 0 && <NoData />}

      {(isLoading || rows.length > 0) && (
        <TableCard data-tour="table">
          <Table>
            <THead>
              <tr>
                <Th>{t('gap-analysis:table.name')}</Th>
                <Th>{t('gap-analysis:table.status')}</Th>
                <Th>{t('gap-analysis:table.date')}</Th>
                <Th>{t('gap-analysis:table.lastUpdated')}</Th>
                <Th>{t('common:labels.description')}</Th>
                <Th $center>{t('gap-analysis:table.actions')}</Th>
              </tr>
            </THead>
            <tbody>
              {rows.map(item => (
                <Tr key={item._id} onClick={() => navigate(`/gap-analysis/show/${item._id}`)}>
                  <TdName>{item.name}</TdName>
                  <TdStatus>
                    <StatusWrap>
                      <StatusDot $color={STATUS_COLORS[item.status] ?? MUTED} />
                      <StatusLabel>{t(`gap-analysis:status.${item.status}`, { defaultValue: item.status })}</StatusLabel>
                    </StatusWrap>
                  </TdStatus>
                  <Td>{formatDate(item.date)}</Td>
                  <Td>{formatDate(item.last_updated)}</Td>
                  <TdDesc title={item.description}>{item.description || '—'}</TdDesc>
                  <ActionCell onClick={e => e.stopPropagation()}>
                    <ActionsWrap>
                      <IconBtn type="button" title="Ver" onClick={() => navigate(`/gap-analysis/show/${item._id}`)}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                        </svg>
                      </IconBtn>
                      <IconBtn type="button" title="Dashboard" onClick={() => navigate(`/gap-analysis/dashboard/${item._id}`)}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="18" y1="20" x2="18" y2="10"/>
                          <line x1="12" y1="20" x2="12" y2="4"/>
                          <line x1="6" y1="20" x2="6" y2="14"/>
                        </svg>
                      </IconBtn>
                    </ActionsWrap>
                  </ActionCell>
                </Tr>
              ))}
            </tbody>
          </Table>
        </TableCard>
      )}
    </Container>
  );
};
